<template lang="pug">
UpsertPage(
  :title="title" 
  @save="save"
  @cancel="$emit('cancel')"
  :disabled="disabled"
  :pending="isSaving"
  :showAuditTrail="userCanViewAuditTrail && scheduleItem.Key !== undefined"
  objectType="ResourceSchedule"
  :objectKey="scheduleItem.Key"
  )
  .upsert-schedule-fields
    BriqPanel
      h2.mb-S Settings
      BriqInput(
        label="Description"
        v-model="scheduleItem.Description"
        optional
        help="Description of the setting, for example 'Christmas holiday'"
        :disabled="isSaving"
        data-test-id="description"
        )
      BriqCheckButtons(
        label="Applicable weekdays"
        :options="weekdays"
        help="Select the weekdays this schedule rule applies to"
        v-model="scheduleItem.Weekdays"
        required="true"
        :disabled="isSaving"
        :error="weekdaysError"
        )
      BriqMultiselect(
        label="Resources"
        :options="filteredResources"
        v-model="selectedResources"
        track-by="Key"
        display="Description"
        :multiple="true"
        :close-on-select="false",
        bindObject
        :required="true"
        :disabled="isSaving"
        :allowToggleAll="true"
        :error="resourcesError"
        )
      .flex
        BriqDateTimePicker(
          name="from"
          id="from"
          label="Applicable from"
          v-model="scheduleItem.StartDate"
          placeholder="From date"
          autocomplete="off"
          data-test-id="schedule-item-start-date"
          :showClearButton="true"
          :error="applicableDatesError"
        )
        BriqDateTimePicker(
          name="until"
          id="until"
          label="Applicable until"
          v-model="scheduleItem.EndDate"
          placeholder="Until date"
          autocomplete="off"
          data-test-id="schedule-item-end-date"
          :showClearButton="true"
        )
    BriqPanel
      h2.mb-S Changes
      .flex
        BriqTimeInput(
          label="Start time"
          required="required"
          data-test-id="start-time"
          v-model="scheduleItem.StartTime"
          :disabled="isSaving"
          style="margin-right: 10px"
          :error="startTimeError"
          :twelve-hour-clock="$twelvehourclock"
          :emitSeconds="true"
          )
        BriqTimeInput(
          label="End time"
          required="required"
          data-test-id="end-time"
          v-model="scheduleItem.EndTime"
          :disabled="isSaving"
          :error="endTimeError"
          :twelve-hour-clock="$twelvehourclock"
          :emitSeconds="true"
          )
      .validation-message.start-end-time-validation-message(v-if="startEndTimeError" data-test-id="schedule-start-end-time-inputs-error") {{ startEndTimeError }}
      BriqRadioButtons(
        label="Availability"
        v-model="available"
        required="required"
        help="With 'available' the activity is available at the chosen time, with 'unavailable' the activity is not available at the chosen time."
        :options="['Open', 'Closed']"
        :error="availableError"
        :disabled="isSaving"
        data-test-id="availability"
        )
      BriqDurationInput(
        label="Timeslot Duration"
        v-model="scheduleItem.BookingLength"
        optional
        :disabled="isSaving"
        help="The adjusted length of a timeslot. This field overrules the timeslot duration of the resource, for the duration of this rule."
        data-test-id="timeslot-duration"
        )
      BriqInput(
        label="Capacity"
        v-model="scheduleItem.Capacity"
        optional
        type="number"
        :disabled="isSaving"
        help="The adjusted capacity of the activity. This field overrules the capacity of the resource, for the duration of this rule."
        data-test-id="capacity"
        )
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators'
import { includes, pipe, prop, flip, map, filter } from 'ramda'
import UpsertPage from '@v/components/UpsertPage.vue'
import BriqDurationInput from '@v/components/BriqDurationInput.vue'
import BriqDateTimePicker from '@/vue/components-global/BriqDateTimePicker.vue'
import BriqCheckButtons from '@v/components/BriqCheckButtons.vue'
import BriqRadioButtons from '@v/components/BriqRadioButtons.vue'
import {
  scheduleStartEndTimeValidation,
  startDateIsBeforeOrEqualToEndDate,
} from '@/vue/util/customValidations.js'

export default {
  name: 'UpsertScheduleItem',
  components: {
    UpsertPage,
    BriqDurationInput,
    BriqCheckButtons,
    BriqRadioButtons,
    BriqDateTimePicker,
  },
  props: {
    scheduleItem: { type: Object, required: true },
    resources: { type: Array, required: true },
    isSaving: Boolean,
    disabled: Boolean,
    userCanViewAuditTrail: Boolean,
    startOfDay: { type: String, required: true },
  },
  data() {
    return {
      weekdays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      innerSelectedResources: [],
    }
  },
  validations() {
    return {
      scheduleItem: {
        ResourceKeys: { required, minLength: minLength(1) },
        Weekdays: { required, minLength: minLength(1) },
        StartTime: {
          required,
          startEndTimeValidation: scheduleStartEndTimeValidation(this.startOfDay),
        },
        EndTime: { required },
        Available: { required },
        StartDate: { startDateIsBeforeOrEqualToEndDate },
      },
    }
  },
  computed: {
    filteredResources() {
      return this.resources.filter(item => item.ResourceType !== 'ResourceGroup')
    },
    title() {
      return this.scheduleItem.Key ? 'Edit item' : 'Create item'
    },
    selectedResources: {
      get() {
        return this.innerSelectedResources
      },
      set(v) {
        this.innerSelectedResources = v
        this.scheduleItem.ResourceKeys = map(prop('Key'))(v)
      },
    },
    dateRange: {
      get() {
        if (!this.scheduleItem.StartDate && !this.scheduleItem.EndDate) return []
        if (!this.scheduleItem.EndDate) return [this.scheduleItem.StartDate]
        return [this.scheduleItem.StartDate, this.scheduleItem.EndDate]
      },
      set(v) {
        this.scheduleItem.StartDate = v.length > 0 ? v[0] : null
        this.scheduleItem.EndDate = v.length > 0 ? v[1] : null
      },
    },
    available: {
      get() {
        if (this.scheduleItem.Available === null) return null
        return this.scheduleItem.Available === true ? 'Open' : 'Closed'
      },
      set(v) {
        this.scheduleItem.Available = v === 'Open'
      },
    },
    // vuelidate errors
    resourcesError() {
      const showError = this.$v.$dirty && this.$v.scheduleItem.ResourceKeys.$invalid
      return showError ? 'Please select one or more resources' : null
    },
    weekdaysError() {
      const showError = this.$v.$dirty && this.$v.scheduleItem.Weekdays.$invalid
      return showError ? 'Please select one or more weekdays' : null
    },
    applicableDatesError() {
      const showError =
        this.$v.$dirty &&
        this.$v.scheduleItem.StartDate.$invalid &&
        !this.$v.scheduleItem.StartDate.startDateIsBeforeOrEqualToEndDate

      return showError ? 'The applicable from date should be before applicable until date' : null
    },
    startTimeError() {
      const showError =
        this.$v.$dirty &&
        this.$v.scheduleItem.StartTime.$invalid &&
        this.$v.scheduleItem.StartTime.startEndTimeValidation
      return showError ? 'Start time is required' : null
    },
    startEndTimeError() {
      const showError = this.$v.$dirty && this.$v.scheduleItem.StartTime.$invalid
      const startEndTimeError = `Please make sure that the start time precedes the end time within 24 hours of the start of day time. Your start of day time is configured at ${this.startOfDay}.`
      return showError && !this.$v.scheduleItem.StartTime.startEndTimeValidation
        ? startEndTimeError
        : null
    },
    endTimeError() {
      const showError = this.$v.$dirty && this.$v.scheduleItem.EndTime.$invalid
      return showError ? 'End time is required' : null
    },
    availableError() {
      const showError = this.$v.$dirty && this.$v.scheduleItem.Available.$invalid
      return showError ? 'Availability is required' : null
    },
  },
  methods: {
    save() {
      this.$v.$touch()
      if (this.$v.$invalid) this.$notifyWarning('Validation failed, please doublecheck all fields')
      else this.$emit('save', this.scheduleItem)
    },
  },
  mounted() {
    const filterSelected = filter(pipe(prop('Key'), flip(includes)(this.scheduleItem.ResourceKeys)))
    this.innerSelectedResources = filterSelected(this.resources)
  },
}
</script>

<style scoped lang="stylus">
.mr-10
  margin-right 10px
</style>

<style lang="stylus">
.upsert-schedule-fields .briq-form-element
  margin-bottom 24px

#app
  background transparent

.start-end-time-validation-message
  margin-bottom 24px
  margin-top -18px
</style>
